import { ComicPageLoader } from "@vapor/react-contrib-tax-compliance";
import { useLayoutEffect } from "react";
import { useGetSubscriptions } from "../../core/api/use-get-subscriptions/useGetSubscriptions";
import { PermissionsGate } from "../../core/permissions/PermissionsGate";
import { PermissionsProvider } from "../../core/permissions/PermissionsProvider";
import { WorkspaceIdProvider } from "../../core/workspace/WorkspaceIdProvider";
import { useTenantWorkspace } from "../../core/workspace/useTenantWorkspace";
import { sessionStoreSlice } from "../../store/sessionStoreSlice";
import { ServiceNotActive } from "../components/service-not-active/ServiceNotActive";
import { DataNotAvailable } from "../components/data-not-available/DataNotAvailable";

interface ViewWrapperProps {
    children: React.ReactNode;
}

export const ViewWrapper: React.FC<ViewWrapperProps> = ({
    children,
}): React.JSX.Element => {
    // The TENANT workspace (the current organization's own), not the one selected
    // in the shell: see useTenantWorkspace.
    const { workspace, isLoading: isLoadingWorkspace } = useTenantWorkspace();
    const {
        data: subscriptions,
        isPending: isLoadingSubscriptions,
        isError: isSubscriptionsError,
    } = useGetSubscriptions(workspace?.id);

    const activeSubscription = !!subscriptions?.some(
        (subscription) => subscription.isActive === true,
    );

    // A LAYOUT effect, not a passive one, and the difference is visible: Home
    // reads `session.subscriptions` out of the store during render and derives
    // the upload button's disabled state from it with
    // `subscriptions.every((sub) => sub.isReadOnly)` — which is vacuously TRUE
    // on the default empty array. A passive effect lets the browser paint that
    // frame, so the button flashes disabled before the store is seeded. A
    // layout effect runs before the paint and its store write is flushed
    // synchronously (`useSelector` is built on
    // `useSyncExternalStoreWithSelector`), so the frame never reaches the
    // screen.
    //
    // The real fix is for those consumers to read the query instead of this
    // copy of it, at which point the write — and this effect — go away.
    useLayoutEffect(() => {
        if (activeSubscription && subscriptions) {
            sessionStoreSlice.updateValues({ subscriptions });
        }
    }, [activeSubscription, subscriptions]);

    if (isLoadingWorkspace) return <ComicPageLoader loading={true} />;

    // Loading is over and there is still no tenant workspace, so the app has
    // nothing to operate on and says so instead of staying stuck on the loader.
    //
    // Which of the possible causes it was is NOT knowable from here, and the
    // hook cannot tell them apart either: @ice/core never puts a failed
    // `retrieveWorkspaces` into the snapshot, so a list that failed to load
    // looks exactly like an organization that owns no workspace. Both reach
    // this branch, but only after useTenantWorkspace's deadline runs out;
    // a list that landed with nothing matching the organization, and a failed
    // workspace SUBSCRIPTION, arrive here immediately. See useTenantWorkspace.
    if (!workspace) return <DataNotAvailable />;

    // A failed subscriptions fetch is NOT "service not active": without
    // initialData, data stays undefined, so the two cases remain
    // distinguishable. It must also not be confused with a failed BACKGROUND
    // refetch — `refetchOnReconnect` is on (only `refetchOnWindowFocus` is
    // disabled on the QueryClient), and React Query keeps serving the previous
    // `data` while `isError` is true. Tearing the whole app down for that would
    // throw away a working page over a network blip, so the terminal branch
    // needs BOTH: the fetch failed AND there is nothing to render.
    if (isSubscriptionsError && subscriptions === undefined)
        return <DataNotAvailable />;

    return (
        // The workspace is resolved here; handing it down by context means every
        // hook in the subtree reads it on its first render.
        <WorkspaceIdProvider workspaceId={workspace.id}>
            {/* Mounted as soon as the workspace is known, ABOVE the
                subscriptions loader: the policy round trip overlaps that
                fetch instead of queueing behind it. The cost is that
                policies get evaluated even for a user with no active
                service. */}
            <PermissionsProvider>
                <ComicPageLoader loading={isLoadingSubscriptions}>
                    {activeSubscription ? (
                        <PermissionsGate>{children}</PermissionsGate>
                    ) : (
                        <ServiceNotActive />
                    )}
                </ComicPageLoader>
            </PermissionsProvider>
        </WorkspaceIdProvider>
    );
};
